import dayjs from 'dayjs';

import { useEffect, useState } from 'react';

import { Col, DatePicker, Input, message, Row, Space, Upload, Button } from 'antd';

import { CommonModalFooter, ModalWrapper } from './index';
import CallApi from '../../API_Utils/APIUtil/CallApi';
import { getLatestLrNumber, showErrorMessage } from '../../clientUtils';
import * as Constants from '../../constants';

const UpdateLrModal = ({
  isOpen,
  onCancel,
  onSuccess,
  apiEndpoint,
  apiParams,
  defaultLrNumber = '',
  defaultLrDate = null,
  eWayBillDate = null,
  deliveryDate = null
}) => {
  const [loading, setLoading] = useState(false);
  const [lrDate, setLrDate] = useState(null);
  const [lrNumber, setLrNumber] = useState('');
  const [comment, setComment] = useState('');
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setLrNumber(getLatestLrNumber(defaultLrNumber) || '');
      setLrDate(defaultLrDate ? dayjs(defaultLrDate) : null);
      setComment('');
      setFile(null);
    }
  }, [isOpen, defaultLrNumber, defaultLrDate]);

  const disabledDate = (current) => {
    if (!current) return false;

    if (eWayBillDate && current.isBefore(dayjs(eWayBillDate), 'day')) {
      return true;
    }

    if (deliveryDate && current.isAfter(dayjs(deliveryDate), 'day')) {
      return true;
    }

    return false;
  };

  const validateLrDate = () => {
    if (!lrDate) return false;

    if (eWayBillDate && lrDate.isBefore(dayjs(eWayBillDate), 'day')) {
      message.error('LR date cannot be before E-Way Bill date');
      return false;
    }

    if (deliveryDate && lrDate.isAfter(dayjs(deliveryDate), 'day')) {
      message.error('LR date cannot be after delivery date');
      return false;
    }

    return true;
  };

  const handleBeforeUpload = (selectedFile) => {
    const isLt5M = selectedFile.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Error: File size exceeds 5 MB');
      return false;
    }
    if (selectedFile.type !== 'application/pdf') {
      message.error('You can only upload PDF files');
      return false;
    }
    setFile(selectedFile);
    return false;
  };

  const handleFileRemove = () => {
    setFile(null);
  };

  const isSubmitDisabled = () =>
    loading || !lrDate || !lrNumber?.trim() || !comment?.trim() || !file;

  const handleClose = () => {
    setLrDate(null);
    setLrNumber('');
    setComment('');
    setFile(null);
    onCancel();
  };

  const handleSubmit = () => {
    if (!validateLrDate()) return;

    const dateStr = lrDate.format('YYYY-MM-DD');
    const numberStr = lrNumber.trim();

    const formData = new FormData();
    formData.append('comment', comment.trim());
    formData.append('file', file);

    CallApi(
      apiEndpoint,
      {
        ...apiParams,
        receiptDate: dateStr,
        receiptNumber: numberStr
      },
      formData,
      {
        start: () => setLoading(true),
        stop: () => setLoading(false)
      },
      () => {
        message.success('LR details updated successfully');
        handleClose();
        onSuccess?.();
      },
      showErrorMessage
    );
  };

  return (
    <ModalWrapper
      content={
        <Space className="w-100" direction="vertical" size="large">
          <Row>
            <Col className="required-after" span={8}>
              LR Date
            </Col>
            <Col span={16}>
              <DatePicker
                className="w-100"
                disabledDate={disabledDate}
                format={Constants.DATE_FORMAT}
                onChange={(date) => setLrDate(date)}
                value={lrDate}
              />
            </Col>
          </Row>
          <Row>
            <Col className="required-after" span={8}>
              LR Number
            </Col>
            <Col span={16}>
              <Input onChange={(e) => setLrNumber(e.target.value)} value={lrNumber} />
            </Col>
          </Row>
          <Row>
            <Col className="required-after" span={8}>
              Comment
            </Col>
            <Col span={16}>
              <Input.TextArea
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                value={comment}
              />
            </Col>
          </Row>
          <Row>
            <Col className="required-after" span={8}>
              LR File
            </Col>
            <Col span={16}>
              <Upload
                accept="application/pdf"
                beforeUpload={handleBeforeUpload}
                fileList={
                  file
                    ? [
                        {
                          uid: '-1',
                          name: file.name,
                          status: 'done'
                        }
                      ]
                    : []
                }
                maxCount={1}
                onRemove={handleFileRemove}>
                <Button disabled={!!file} size="large">
                  Upload PDF
                </Button>
              </Upload>
            </Col>
          </Row>
        </Space>
      }
      footer={
        <CommonModalFooter
          ifSubmitDisabled={isSubmitDisabled()}
          loading={loading}
          onCancel={handleClose}
          onSubmit={handleSubmit}
          submitButtonLabel="Update"
        />
      }
      onCancel={handleClose}
      open={isOpen}
      title="Update LR Details"
    />
  );
};

export default UpdateLrModal;
